import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext'; // Importér LanguageContext
import HeroSectionV2 from '../../components/HeroSectionV2';
import Billede1 from '../../Images/Billede1.jpg';
import FunctionList from '../../components/FunctionList';
import ShiftsList from '../../components/ShiftsList';
import { Title, Button, Center, Loader, Text } from '@mantine/core';
import EventUserDashboard from "../../components/EventUserDashboard";
import AllAvailableShifts from "../../components/AllAvailableShifts";
import { getAuthHeaders } from "../../api/authHelper";
interface Event {
    eventId: number;
    eventName: string;
    description: string;
    eventLogoFilID: string;
}

interface Functions {
    functionId: number;
    functionName: string;
    description?: string;
    eventID?: number;
    functionCategoryId?: number;
}

const EventPage: React.FC = () => {
    const { eventId, functionsId } = useParams<{ eventId: string, functionsId?: string }>();
    const { language } = useLanguage(); // Brug sprog fra LanguageContext
    const [eventData, setEventData] = useState<Event | null>(null);
    const [Functions, setFunctions] = useState<Functions[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedFunctions, setSelectedFunctions] = useState<Functions | null>(null);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);

    const bumpRefresh = () => setRefreshKey((k) => k + 1);

    useEffect(() => {
        if (eventId) {
            const apiUrl = process.env.REACT_APP_API_URL;

            // Fetch event data
            fetch(`${apiUrl}/api/events/${eventId}/light`)
                .then((response) => response.json())
                .then((data) => setEventData(data))
                .catch((error) => setError('Failed to fetch event data.'));

            // Fetch functions for the event
            fetch(`${apiUrl}/api/events/${eventId}/functions`, {
                headers: getAuthHeaders(),
            })
                .then((response) => response.json())
                .then((data) => setFunctions(data))
                .catch((error) => setError('Failed to fetch Functions data.'))
                .finally(() => setLoading(false));
        }
    }, [eventId]);

    useEffect(() => {
        if (!functionsId || functionsId === "available") {
            setSelectedFunctions(null);
            return;
        }

        if (Functions.length > 0) {
            const functions = Functions.find((g) => g.functionId === Number(functionsId));
            setSelectedFunctions(functions || null);
        }
    }, [functionsId, Functions]);

    if (error) {
        return <p>{error}</p>;
    }

    return (
        <div>
            <HeroSectionV2
                titleEn={eventData ? `${eventData.eventName}` : "Loading...1"}
                titleDa={eventData ? `${eventData.eventName}` : "Loading...1"}
                descriptionEn={"On this page you can register your shifts."}
                descriptionDa={"Her på side kan du tilmelde sig dine vagter"}
                imageSrc={Billede1}
                badgeText={language === 'da' ? 'Tilmeld' : 'Sign up'}
            />

            {loading &&
               <Center mt="lg" style={{ flexDirection: 'column' }}>
                        <Loader color="red" size="lg" />
                        <Text mt="sm">{language === 'da' ? 'Indlæser...' : 'Loading...'}</Text>
               </Center>
            }

            <EventUserDashboard
                eventId={eventId ? Number(eventId) : undefined}
                language={language}
                defaultCollapsed={true}
                refreshKey={refreshKey}  
                onChanged={bumpRefresh}
            />
            {selectedFunctions && (
                <>
                    <Center my="md" style={{ flexDirection: 'column' }}>
                        <Title order={2} mb="xs">
                            {language === 'da'
                                ? `Vagter i ${selectedFunctions.functionName}`
                                : `Shifts in ${selectedFunctions.functionName}`}
                        </Title>

                        <Button
                            variant="light"
                            size="xs"
                            color="gray"
                            onClick={() => {
                                setSelectedFunctions(null);
                                navigate(`/EventPage/${eventId}`);
                            }}
                        >
                            {language === 'da' ? '← Tilbage til funktioner' : '← Back to functions'}
                        </Button>
                    </Center>

                </>
            )}
    
            {/* Brug GroupList og pas language automatisk */}
            {functionsId ? (
                functionsId === "available" ? (
                    <AllAvailableShifts
                        eventId={Number(eventId)}
                        language={language}
                        onRegistrationChanged={bumpRefresh}
                    />
                ) : (
                    <ShiftsList
                        groupId={Number(functionsId)}
                        language={language}
                        eventId={Number(eventId)}
                        onRegistrationChanged={bumpRefresh}
                    />
                )
            ) : (
                <>
                    <Center my="md" style={{ flexDirection: "column", gap: 8 }}>
                            <Button
                                variant="subtle"
                                radius="xl"
                                size="xs"
                                color="red"
                                styles={{
                                    root: {
                                        border: "1px solid #ffb3b3",
                                        backgroundColor: "#ffe5e5",
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                    }
                                }}
                                onClick={() => navigate(`/EventPage/${eventId}/available`)}
                            >
                                {language === "da" ? "Se alle ledige vagter" : "See all available shifts"}
                        </Button>
                    </Center>

                    <FunctionList
                        functions={Functions}
                        language={language}
                        eventID={Number(eventId)}
                    />
                </>
            )}
        </div>
    );
};

export default EventPage;
